import { useApolloClient } from "@apollo/client";
import {
  CardContainer,
  LabeledTextValue,
  LabeledValue,
  TagChips,
} from "@msys/ui";
import { Divider, Link as MuiLink, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { isNull, omit } from "lodash-es";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import {
  CrmOrganisationAbcCategory,
  CrmOrganisationContactType,
  namedOperations,
  PhoneInput,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { EntityNumber } from "../../../commons/EntityNumber.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { DetachedEmailField } from "../../../commons/form-fields/EmailField.js";
import {
  PHONE_REG_EXP,
  prepareUrl,
  SITE_REG_EXP,
} from "../../../commons/form-fields/helpers.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TagsField } from "../../../commons/form-fields/TagsField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { AddressComboField } from "../../addresses/AddressComboField.js";
import { AddressDetails__AddressFragment } from "../../addresses/Addresses.generated.js";
import { LabeledAddressValue } from "../../addresses/LabeledAddressValue.js";
import { Attachment } from "../../attachments/helpers.js";
import { CategoriesInput } from "../../categories/CategoriesInput.js";
import { CategoriesLabeledValue } from "../../categories/CategoriesLabeledValue.js";
import { useEmailAddressValidation } from "../../email/validateEmailAddress.js";
import { OrganisationAvatar } from "../../organisations/OrganisationAvatar.js";
import { useOrganisationTags } from "../../organisations/useOrganisationTags.js";
import { LabeledPhoneValue } from "../../phones/LabeledPhoneValue.js";
import { PhonesField } from "../../phones/PhonesField.js";
import { defaultPhoneType } from "../../phones/usePhoneTypes.js";
import { useCrmOrganisationAbcCategories } from "../hooks/useCrmOrganisationAbcCategories.js";
import { useCrmOrganisationContactTypes } from "../hooks/useCrmOrganisationContactTypes.js";
import {
  CrmCompanyAvatarAndTitleData_CrmCompanyFragment,
  CrmCompanyDetailsBox_CrmCompanyFragment,
  CrmCompanyDetailsData_CrmCompanyFragment,
  CrmCompanyDetailsForm_CrmCompanyFragment,
  useCrmCompanyDetailsForm_ModifyCrmCompanyMutation,
  useModifyCrmOrganisationAvatarMutation,
} from "./CrmCompanyDetailsBox.generated.js";

interface Props {
  crmCompany: CrmCompanyDetailsBox_CrmCompanyFragment;
}

export function CrmCompanyDetailsBox({ crmCompany }: Props) {
  return (
    <CardContainer>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          p: 1,
        }}
      >
        <RestrictedByOrganisationPermissionWithDebug
          permission="MANAGE_CRM"
          otherwise={<CrmCompanyAvatarAndTitleData crmCompany={crmCompany} />}
        >
          <CrmCompanyAvatarAndTitleForm crmCompany={crmCompany} />
        </RestrictedByOrganisationPermissionWithDebug>
        <RestrictedByOrganisationPermissionWithDebug
          permission="MANAGE_CRM"
          otherwise={<CrmCompanyDetailsData crmCompany={crmCompany} />}
        >
          <CrmCompanyDetailsForm crmCompany={crmCompany} />
        </RestrictedByOrganisationPermissionWithDebug>
      </Stack>
    </CardContainer>
  );
}

export function CrmCompanyAvatarAndTitleData({
  crmCompany,
}: {
  crmCompany: CrmCompanyAvatarAndTitleData_CrmCompanyFragment;
}) {
  return (
    <Stack direction="column" spacing={1}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
        }}
      >
        <OrganisationAvatar organisationAvatar={crmCompany} size="l" />
      </Stack>
      <Stack direction="column" spacing={0.5}>
        <Typography variant="h2" align="center">
          {crmCompany.title}
        </Typography>
        <Typography variant="body2" align="center" color="primary">
          <EntityNumber number={crmCompany.number} />
        </Typography>
      </Stack>
    </Stack>
  );
}

export function CrmCompanyAvatarAndTitleForm({
  crmCompany,
}: {
  crmCompany: CrmCompanyAvatarAndTitleData_CrmCompanyFragment;
}) {
  const client = useApolloClient();
  const [modifyCrmOrganisation, { loading: modifyCrmOrganisationLoading }] =
    useModifyCrmOrganisationAvatarMutation({
      client,
    });
  const onAttachment = async (attachment: Attachment | null) => {
    await modifyCrmOrganisation({
      variables: {
        organisations: [
          {
            id: crmCompany.id,
            logo: attachment
              ? {
                  url: attachment.url,
                  title: attachment.title,
                  mimeType: attachment.mimeType,
                }
              : null,
          },
        ],
      },
    });
  };
  return (
    <Stack direction="column" spacing={1}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
        }}
      >
        <OrganisationAvatar
          organisationAvatar={crmCompany}
          size="l"
          disabled={modifyCrmOrganisationLoading}
          onAttachment={onAttachment}
        />
      </Stack>
      <Stack direction="column" spacing={0.5}>
        <Typography variant="h2" align="center">
          {crmCompany.title}
        </Typography>
        <Typography variant="body2" align="center" color="primary">
          <EntityNumber number={crmCompany.number} />
        </Typography>
      </Stack>
    </Stack>
  );
}

export function CrmCompanyDetailsData({
  crmCompany,
}: {
  crmCompany: CrmCompanyDetailsData_CrmCompanyFragment;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { abcCategoryLabels } = useCrmOrganisationAbcCategories();
  const { contactTypeLabels } = useCrmOrganisationContactTypes();
  const isCraftsman = crmCompany.systemTags.includes("CRAFTSMAN");
  const isSupplier = crmCompany.systemTags.includes("SUPPLIER");

  return (
    <Stack direction="column" spacing={1}>
      <LabeledValue
        label={t("Account Name", {
          ns: "OrganisationProfile",
        })}
      >
        {crmCompany.title ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <LabeledValue
        label={t("Description", {
          ns: "OrganisationProfile",
        })}
      >
        {crmCompany.description ||
          t("Not set", {
            ns: "Global",
          })}
      </LabeledValue>
      <Divider />
      <LabeledValue
        label={t("Website", {
          ns: "OrganisationProfile",
        })}
      >
        {crmCompany.website ? (
          <MuiLink
            href={prepareUrl(crmCompany.website)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {crmCompany.website}
          </MuiLink>
        ) : (
          t("Not set", {
            ns: "Global",
          })
        )}
      </LabeledValue>
      <LabeledValue
        label={t("Email", {
          ns: "OrganisationProfile",
        })}
      >
        {crmCompany.email ? (
          <MuiLink href={`mailto:${crmCompany.email}`}>
            {crmCompany.email}
          </MuiLink>
        ) : (
          t("Not set", {
            ns: "Global",
          })
        )}
      </LabeledValue>
      <LabeledPhoneValue
        label={t("Phone", {
          ns: "Global",
        })}
        phones={crmCompany.phones}
      />
      {isCraftsman && (
        <>
          <Divider />
          <CategoriesLabeledValue
            categoryIds={crmCompany.categories}
            label={t("Services", {
              ns: "OrganisationProfile",
            })}
          />
        </>
      )}
      <Divider />
      {!isSupplier && (
        <>
          <LabeledValue
            label={t("Contact type", {
              ns: "OrganisationProfile",
            })}
          >
            {contactTypeLabels[crmCompany.contactType]}
          </LabeledValue>

          <LabeledValue
            label={t("ABC Category", {
              ns: "OrganisationProfile",
            })}
          >
            {abcCategoryLabels[crmCompany.abcCategory]}
          </LabeledValue>

          <Divider />

          <LabeledTextValue
            label={t("Notes", { ns: "OrganisationProfile" })}
            text={crmCompany.notices}
            notSetLabel={t("Not set", { ns: "Global" })}
            showMoreLabel={t("Show more", { ns: "Global" })}
            showLessLabel={t("Show less", { ns: "Global" })}
          />

          <LabeledValue label={t("Tags", { ns: "OrganisationProfile" })}>
            {crmCompany.customTags.length > 0 ||
            crmCompany.systemTags.length > 0 ? (
              <TagChips
                tags={[...crmCompany.systemTags, ...crmCompany.customTags]}
              />
            ) : (
              "–"
            )}
          </LabeledValue>
        </>
      )}
      <LabeledAddressValue
        label={t("Billing address", {
          ns: "OrganisationProfile",
        })}
        address={crmCompany.billingAddress}
      />
      <LabeledAddressValue
        label={t("Branch address", {
          ns: "OrganisationProfile",
        })}
        address={crmCompany.branchAddress}
      />
      {isSupplier && (
        <LabeledAddressValue
          label={t("Pick-up location", {
            ns: "OrganisationProfile",
          })}
          address={crmCompany.pickupAddress}
        />
      )}
    </Stack>
  );
}

interface FormValues {
  title: string;
  description: string;
  notices: string;
  billingAddress: AddressDetails__AddressFragment | null;
  branchAddress: AddressDetails__AddressFragment | null;
  pickupAddress: AddressDetails__AddressFragment | null;
  phones: PhoneInput[];
  website: string;
  email: string;
  categories: string[];
  tags: { type: "CUSTOM" | "SYSTEM"; value: string }[];
  abcCategory: CrmOrganisationAbcCategory;
  contactType: CrmOrganisationContactType;
}

export function CrmCompanyDetailsForm({
  crmCompany,
  onChange,
}: {
  crmCompany: CrmCompanyDetailsForm_CrmCompanyFragment;
  onChange?: (crmCompany: { title: string }) => void;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const isEmailAddressValid = useEmailAddressValidation();
  const { abcCategoryOptions } = useCrmOrganisationAbcCategories();
  const { contactTypeOptions } = useCrmOrganisationContactTypes();

  const client = useApolloClient();

  const { customTags } = useOrganisationTags("CRM_COMPANY");

  const [modifyCrmOrganisation] =
    useCrmCompanyDetailsForm_ModifyCrmCompanyMutation({
      client,
      refetchQueries: [namedOperations.Query.OrganisationTags],
      awaitRefetchQueries: true,
    });

  const isCraftsman = crmCompany.systemTags.includes("CRAFTSMAN");
  const isSupplier = crmCompany.systemTags.includes("SUPPLIER");

  const initialValues: FormValues = {
    title: crmCompany.title,
    description: crmCompany.description,
    email: crmCompany.email,
    website: crmCompany.website,
    billingAddress: crmCompany?.billingAddress ?? null,
    branchAddress: crmCompany?.branchAddress ?? null,
    pickupAddress: crmCompany?.pickupAddress ?? null,
    phones:
      crmCompany.phones?.length > 0 ? crmCompany.phones : [defaultPhoneType],
    abcCategory: crmCompany.abcCategory,
    contactType: crmCompany.contactType ?? "COMPANY",
    categories: crmCompany ? crmCompany.categories : [],
    notices: crmCompany.notices,
    tags: [
      ...crmCompany.systemTags.map(tag => ({
        type: "SYSTEM" as const,
        value: tag,
      })),
      ...crmCompany.customTags.map(tag => ({
        type: "CUSTOM" as const,
        value: tag,
      })),
    ],
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(
        t("Account Name", {
          ns: "OrganisationProfile",
        })
      )
      .required(),
    email: Yup.string()
      .label(
        t("Email", {
          ns: "OrganisationProfile",
        })
      )
      .email()
      .trim(),
    categories: Yup.array()
      .label(
        t("Services", {
          ns: "OrganisationProfile",
        })
      )
      .when([], {
        is: () => isCraftsman,
        then: schema => schema.required(),
      }),
    website: Yup.string()
      .label(
        t("Website", {
          ns: "OrganisationProfile",
        })
      )
      .matches(
        SITE_REG_EXP,
        t("Website is not valid", {
          ns: "OrganisationProfile",
        })
      ),
    phones: Yup.array().of(
      Yup.object().shape({
        id: Yup.string(),
        main: Yup.boolean(),
        type: Yup.string(),
        number: Yup.string().matches(
          PHONE_REG_EXP,
          t("Phone number is not valid", {
            ns: "OrganisationProfile",
          })
        ),
        // .required(t("Phone number is required")),
      })
    ),
    tags: Yup.array()
      .of(Yup.object().shape({ value: Yup.string() }))
      .label(
        t("Tags", {
          ns: "OrganisationProfile",
        })
      ),
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const result = await modifyCrmOrganisation({
        variables: {
          crmCompanies: [
            {
              id: crmCompany.id,
              title: values.title,
              description: values.description,
              billingAddress: isNull(values.billingAddress)
                ? null
                : values.billingAddress
                  ? {
                      ...omit(values.billingAddress, "__typename", "id"),
                      location: values.billingAddress.location
                        ? omit(values.billingAddress.location, "__typename")
                        : null,
                    }
                  : undefined,
              branchAddress: isNull(values.branchAddress)
                ? null
                : values.branchAddress
                  ? {
                      ...omit(values.branchAddress, "__typename", "id"),
                      location: values.branchAddress.location
                        ? omit(values.branchAddress.location, "__typename")
                        : null,
                    }
                  : undefined,
              pickupAddress: isNull(values.pickupAddress)
                ? null
                : values.pickupAddress
                  ? {
                      ...omit(values.pickupAddress, "__typename", "id"),
                      location: values.pickupAddress.location
                        ? omit(values.pickupAddress.location, "__typename")
                        : null,
                    }
                  : undefined,
              website: values.website,
              email: values.email,
              phones: values.phones
                .filter(p => p.number.trim())
                .map(p => omit(p, "__typename", "id")),
              notices: values.notices,
              tags: values.tags
                .filter(tag => tag.type === "CUSTOM")
                .map(tag => tag.value),
              abcCategory: values.abcCategory,
              contactType: values.contactType,
              categories: values.categories,
            },
          ],
        },
      });

      if (!result.data) {
        throw new Error("Failed to edit organisation");
      }

      onChange?.({ title: values.title });
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {(formikProps: FormikProps<FormValues>) => (
        <Form>
          <Stack direction="column" spacing={1}>
            {isSupplier ? (
              <LabeledValue
                label={t("Account Name", {
                  ns: "OrganisationProfile",
                })}
              >
                {crmCompany.title ||
                  t("Not set", {
                    ns: "Global",
                  })}
              </LabeledValue>
            ) : (
              <TextField
                name="title"
                label={t("Account Name", {
                  ns: "OrganisationProfile",
                })}
                required
                disabled={false}
              />
            )}
            <TextField
              name="description"
              label={t("Description", {
                ns: "OrganisationProfile",
              })}
              disabled={false}
              multiline
            />
            {!isSupplier && (
              <>
                <Divider />
                <SelectField
                  label={t("Contact type", {
                    ns: "OrganisationProfile",
                  })}
                  name="contactType"
                  options={contactTypeOptions}
                  disabled={false}
                />
                <SelectField
                  label={t("ABC Category", {
                    ns: "OrganisationProfile",
                  })}
                  name="abcCategory"
                  options={abcCategoryOptions}
                  disabled={false}
                />
                <TextField
                  multiline
                  name="notices"
                  label={t("Notes", {
                    ns: "OrganisationProfile",
                  })}
                  disabled={false}
                />
                <TagsField
                  inputLabel={t("Tags", {
                    ns: "OrganisationProfile",
                  })}
                  name="tags"
                  options={customTags}
                  // reservedOptions={[]}
                  disabled={false}
                />
              </>
            )}
            <Divider />
            <TextField
              disabled={false}
              name="website"
              label={t("Website", {
                ns: "OrganisationProfile",
              })}
            />
            <DetachedEmailField
              name="email"
              label={t("Email", {
                ns: "OrganisationProfile",
              })}
              validate={isEmailAddressValid}
              isValid={crmCompany.isEmailValid}
            />
            <PhonesField
              name="phones"
              buttonLabel={t("Add phone number", {
                ns: "OrganisationProfile",
              })}
              value={formikProps.values.phones}
              disabled={false}
            />
            {(isCraftsman || isSupplier) && (
              <>
                <Divider />
                <CategoriesInput
                  alwaysShowLabel={true}
                  categoryIds={formikProps.values.categories}
                  onChange={async categories => {
                    await formikProps.setFieldValue(
                      "categories",
                      categories.map(c => c.id)
                    );
                  }}
                  onClear={
                    formikProps.values.categories.length > 0
                      ? async () => {
                          await formikProps.setFieldValue("categories", []);
                        }
                      : undefined
                  }
                  label={t("Services", {
                    ns: "OrganisationProfile",
                  })}
                  disabled={false}
                />
              </>
            )}
            <Divider />
            <AddressComboField
              label={t("Billing address", {
                ns: "OrganisationProfile",
              })}
              address={formikProps.values.billingAddress}
              handleChange={address =>
                formikProps.setFieldValue("billingAddress", address)
              }
            />
            <AddressComboField
              label={t("Branch address", {
                ns: "OrganisationProfile",
              })}
              address={formikProps.values.branchAddress}
              handleChange={address =>
                formikProps.setFieldValue("branchAddress", address)
              }
            />
            {isSupplier && (
              <AddressComboField
                label={t("Pick-up location", {
                  ns: "OrganisationProfile",
                })}
                address={formikProps.values.pickupAddress}
                handleChange={address =>
                  formikProps.setFieldValue("pickupAddress", address)
                }
              />
            )}
            <AutoSave />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}
